import React, { useRef, useState, useEffect } from 'react';
import { Play, Clock, MapPin, Plus } from 'lucide-react';

// ── Lazy Image with Intersection Observer ────────────────────────────────────
const LazyThumbnail = ({ src, alt, className }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = imgRef?.current;
    if (!el) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setInView(true);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '200px 0px', threshold: 0 }
      );
      observer?.observe(el);
      return () => observer?.disconnect();
    } else {
      setInView(true);
    }
  }, []);

  return (
    <div ref={imgRef} className="w-full h-full relative overflow-hidden bg-muted/60">
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse bg-muted"
        />
      )}
      {inView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`${className} transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

// ── Main Grid ─────────────────────────────────────────────────────────────────
const VideoThumbnailGrid = ({ properties, onVideoSelect, onAddToQueue, currentVideo }) => {
  const formatDuration = (duration) => {
    if (!duration) return '5:00';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR')?.format(price) + ' FCFA';
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
          Visites Vidéo Disponibles
        </h2>
        <p className="text-sm text-muted-foreground">
          {properties?.length} propriété{properties?.length !== 1 ? 's' : ''} avec vidéo
        </p>
      </div>

      {/* Responsive grid: 1 col mobile → 2 cols sm → 3 cols lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {properties?.map((property, propertyIndex) => (
          property?.videos?.map((video, videoIndex) => {
            const isCurrentVideo = currentVideo?.property?.id === property?.id &&
              currentVideo?.video?.id === video?.id;

            return (
              <div
                key={`${property?.id}_${video?.id || videoIndex}`}
                className={`group relative bg-card rounded-xl overflow-hidden border border-border transition-all duration-200 hover:shadow-warm ${
                  isCurrentVideo ? 'ring-2 ring-brand-gold' : ''
                }`}
              >
                {/* Video Thumbnail — lazy loaded */}
                <div className="relative aspect-video bg-muted overflow-hidden">
                  <LazyThumbnail
                    src={video?.thumbnail || property?.images?.[0] || '/api/placeholder/400/225'}
                    alt={`${property?.title} - ${video?.title || 'Visite virtuelle'}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />

                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <button
                      onClick={() => onVideoSelect?.(property, video, videoIndex)}
                      className="p-3 sm:p-3.5 bg-white/90 hover:bg-white rounded-full transform group-hover:scale-110 transition-all shadow-lg min-w-[48px] min-h-[48px] flex items-center justify-center touch-manipulation"
                      aria-label={`Lire la vidéo: ${property?.title}`}
                    >
                      <Play className="w-5 h-5 sm:w-6 sm:h-6 text-foreground ml-0.5" />
                    </button>
                  </div>

                  {/* Duration Badge */}
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(video?.duration)}
                  </div>

                  {/* Current Video Indicator */}
                  {isCurrentVideo && (
                    <div className="absolute top-2 left-2 bg-brand-gold text-white px-2 py-1 rounded text-xs font-medium">
                      En cours
                    </div>
                  )}
                </div>

                {/* Property Info */}
                <div className="p-3 sm:p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-foreground text-sm sm:text-base line-clamp-1 flex-1">
                      {property?.title}
                    </h3>
                    <button
                      onClick={() => onAddToQueue?.(property, video)}
                      className="p-1.5 hover:bg-muted rounded-full transition-colors flex-shrink-0 ml-2 min-w-[32px] min-h-[32px] flex items-center justify-center touch-manipulation"
                      title="Ajouter à la file d'attente"
                      aria-label="Ajouter à la file d'attente"
                    >
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>

                  <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground mb-2">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="line-clamp-1">{property?.address}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex gap-2 sm:gap-3 text-muted-foreground">
                      {property?.bedrooms && (
                        <span>{property?.bedrooms} ch</span>
                      )}
                      {property?.bathrooms && (
                        <span>{property?.bathrooms} sdb</span>
                      )}
                      {property?.surface_area && (
                        <span>{property?.surface_area}m²</span>
                      )}
                    </div>

                    <div className="font-semibold text-brand-gold text-xs sm:text-sm">
                      {formatPrice(property?.price)}
                    </div>
                  </div>

                  {/* Video Title */}
                  {video?.title && video?.title !== 'Visite virtuelle' && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {video?.title}
                      </p>
                    </div>
                  )}

                  {/* Property Type & Commune */}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <span className="inline-block px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                      {property?.property_type}
                    </span>

                    {property?.communes?.name && (
                      <span className="text-xs text-muted-foreground">
                        {property?.communes?.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ))}
      </div>
    </div>
  );
};

export default VideoThumbnailGrid;